import { markNotificationBad, markNotificationGood } from "@main/features/session";
import { setAppErrorNotifier } from "@main/infra/handle-app-error";
import { CameraProvider } from "@main/providers/camera-provider";
import { PipControllerProvider } from "@main/providers/pip-controller-provider";
import { sessionLifecycle } from "@main/state/session-lifecycle";
import { dismissToast, getToasts, pushToast } from "@main/state/toast-store";
import { ToastStack } from "@main/ui/toast";
import { onMount, type ParentProps } from "solid-js";

/**
 * §5.2「共有リソースの保持と画面遷移」: 親 layout 配下に共有リソースの Provider を集約する。
 * カメラ MediaStream は CameraProvider が単一スコープで保持する。 起動直後は stream
 * は null で、 セッション開始 (UC-01) のクリックハンドラ内で初めて acquire()
 * が呼ばれる。
 *
 * AppError 通知 (handleAppError) はトーストにルーティングする (M1 で setAppErrorNotifier
 * を expose してある)。
 */
export function AppLayout(props: ParentProps) {
  onMount(() => {
    setAppErrorNotifier((message) => {
      pushToast({ message });
    });
  });

  return (
    <CameraProvider>
      <PipControllerProvider
        shouldAutoClose={() => sessionLifecycle() === "idle" || sessionLifecycle() === "completed"}
        shouldKeepOpen={() => sessionLifecycle() === "running"}
      >
        {/* アプリの基盤面: viewport を最低でも 1 画面分 bg トークン (= 一番外側の落ち着いた地色) で
            塗り、 page content を grow させて画面下端に著作権フッタを常駐させる。 surface ではなく bg
            なので、 短い page でも content の下が連続した地色に見える (未実装の余白に見えない)。 */}
        <div class="flex min-h-dvh flex-col bg-bg">
          {/* page content 領域。 grow で著作権フッタを画面下端へ押す。 flex-col にしておくと page 側の
              <main> が flex-1 で「フッタを除いた viewport 高さ」 を埋められる (= /session の縦中央寄せ)。
              [&>*]:w-full = 唯一の子 (= page の <main>) に width:100% を渡し、 各自の max-w で頭打ち →
              mx-auto 中央寄せ を成立させる (flex item は mx-auto があると stretch しないため content 幅
              に縮んでしまう。 これが「max-w を上げても bar が広がらない」 原因だった)。 */}
          <div class="flex grow flex-col [&>*]:w-full">{props.children}</div>
          <footer class="shrink-0 px-6 py-3 text-center text-[11px] text-ink-4">
            © {new Date().getFullYear()} くちピタ
          </footer>
        </div>
        <ToastStack
          toasts={getToasts}
          onMarkBad={markNotificationBad}
          onMarkGood={markNotificationGood}
          onDismiss={dismissToast}
        />
      </PipControllerProvider>
    </CameraProvider>
  );
}
